import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api, wsUrl, getSessionToken } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "../lib/notify";
import { t, trServer, useLang } from "../lib/i18n";
import { LanguageSwitch } from "../components/LanguageSwitch";
import { BrandBackground } from "../components/BrandBackground";
import FriendsWaitingRoom from "../components/room/FriendsWaitingRoom";
import OnlineWaitingRoom from "../components/room/OnlineWaitingRoom";
import { useTopBanner } from "../lib/ads";
import { matStyle, useMyTableTheme } from "../lib/tableThemes";

// Two tries to take the seat again are at least this far apart (takeSeat).
const SEAT_RETRY_MS = 2000;

// A state of a waiting room in which the player has no seat (not yet, or no longer).
function seatMissing(state, userId) {
  const room = state?.room;
  return !!room && !!userId && !room.started && !(room.members || []).some((m) => m.user_id === userId);
}

export default function Room() {
  const { roomId } = useParams();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  useLang();
  // The ad at the top (apps only): the same banner as the Lobby and the game, kept from one page to
  // the next (lib/ads.js). The band shows the player's felt (changed live by the colour grid below).
  const bannerHeight = useTopBanner();
  const tableThemeId = useMyTableTheme();
  const wsRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimerRef = useRef(null);
  const wsClosedByUserRef = useRef(false);
  const stateRef = useRef(null);
  stateRef.current = state;
  const leavingRef = useRef(false);
  const seatingRef = useRef(false);
  const lastSeatTryRef = useRef(0);
  const userId = user?.user_id;
  useEffect(() => {
    leavingRef.current = false;
    lastSeatTryRef.current = 0;
  }, [roomId]);

  // The player's seat: POST /join (« already in » changes nothing). Taken again whenever a state
  // of the waiting room comes without them: the server takes the seat of a page whose socket
  // stayed away a few seconds (app in the background, network lost). This page draws the player
  // on their seat anyway, and used to stay so, counted by nobody, until it was reloaded.
  const takeSeat = useCallback(async () => {
    if (!userId || seatingRef.current || leavingRef.current || wsClosedByUserRef.current) return;
    if (Date.now() - lastSeatTryRef.current < SEAT_RETRY_MS) return; // the 15 s check tries again
    seatingRef.current = true;
    lastSeatTryRef.current = Date.now();
    try {
      await api.post(`/rooms/${roomId}/join`);
    } catch (e) {
      const status = e?.response?.status;
      if ((status !== 400 && status !== 404) || leavingRef.current) return; // network: tried again later
      // Refused (the table filled up, the game started without them…). A game that has just
      // started with the player in it goes to the game page instead.
      try {
        const { data } = await api.get(`/rooms/${roomId}`);
        const r = data.state?.room;
        if (r?.started) { navigate(`/game/${roomId}`, { replace: true }); return; }
        if (r?.members?.some((m) => m.user_id === userId)) { setState(data.state); return; }
      } catch {}
      const detail = e?.response?.data?.detail;
      if (detail) toast.error(trServer(detail));
      wsClosedByUserRef.current = true;
      navigate("/lobby", { replace: true });
    } finally {
      seatingRef.current = false;
    }
  }, [roomId, userId, navigate]);

  const refetchState = useCallback(async () => {
    try {
      const { data } = await api.get(`/rooms/${roomId}`);
      setState(data.state);
      if (data.state?.room?.started) navigate(`/game/${roomId}`, { replace: true });
    } catch (e) {
      if (e?.response?.status === 404) {
        wsClosedByUserRef.current = true;
        navigate("/lobby", { replace: true });
      }
    }
  }, [roomId, navigate]);

  const connect = useCallback(() => {
    const token = getSessionToken();
    if (!token) return null;
    let ws;
    try { ws = new WebSocket(wsUrl(roomId, token)); } catch { return null; }
    wsRef.current = ws;
    ws.onopen = () => { reconnectAttemptsRef.current = 0; refetchState(); };
    ws.onmessage = (ev) => {
      try {
        if (ev.data === "pong") return;
        const msg = JSON.parse(ev.data);
        if (msg.type === "state") {
          setState(msg.data);
          if (msg.data?.room?.started) navigate(`/game/${roomId}`, { replace: true });
        } else if (msg.type === "error") {
          wsClosedByUserRef.current = true;
          navigate("/lobby", { replace: true });
        }
      } catch {}
    };
    ws.onclose = () => {
      wsRef.current = null;
      if (wsClosedByUserRef.current) return;
      const attempt = reconnectAttemptsRef.current + 1;
      reconnectAttemptsRef.current = attempt;
      const delay = Math.min(1000 * 2 ** (attempt - 1), 8000);
      reconnectTimerRef.current = setTimeout(() => connect(), delay);
    };
    ws.onerror = () => { try { ws.close(); } catch {} };
    return ws;
  }, [roomId, navigate, refetchState]);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      const redirectUrl = window.location.pathname + window.location.search;
      window.location.href = `/?redirect=${encodeURIComponent(redirectUrl)}`;
      return;
    }
    api.get(`/rooms/${roomId}`).then(({ data }) => {
      setState(data.state);
      if (data.state?.room?.started) navigate(`/game/${roomId}`, { replace: true });
    }).catch(() => navigate("/lobby"));
    wsClosedByUserRef.current = false;
    takeSeat();
    connect();
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        const ws = wsRef.current;
        if (!ws || ws.readyState !== 1) connect();
        refetchState();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    window.addEventListener("online", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
      window.removeEventListener("online", onVisible);
      wsClosedByUserRef.current = true;
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      const ws = wsRef.current;
      if (ws) { try { ws.close(); } catch {} }
    };
  }, [roomId, user, loading, connect, navigate, refetchState, takeSeat]);

  useEffect(() => {
    if (!user) return;
    const t = setInterval(() => {
      const ws = wsRef.current;
      if (ws?.readyState === 1) {
        try { ws.send("ping"); } catch {}
      } else if (ws?.readyState !== 0) {
        connect();
      }
      if (seatMissing(stateRef.current, user.user_id)) takeSeat();
    }, 15000);
    return () => clearInterval(t);
  }, [user, connect, takeSeat]);

  // A state of the waiting room without the player: the seat is taken again (takeSeat).
  const missing = seatMissing(state, userId);
  useEffect(() => {
    if (missing) takeSeat();
  }, [state, missing, takeSeat]);

  if (loading || !user || !state) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <motion.div
          className="h-7 w-7 rounded-full border-2 border-amber-400/30 border-t-amber-400"
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
        />
      </div>
    );
  }

  const room = state.room;

  const handleStart = async () => {
    try { await api.post(`/rooms/${roomId}/start`); }
    catch (e) { toast.error(trServer(e?.response?.data?.detail) || t("room.cantStart")); }
  };

  const handleBots = async (newBotCount) => {
    try { await api.post(`/rooms/${roomId}/bots`, { num_bots: newBotCount }); }
    catch (e) { toast.error(trServer(e?.response?.data?.detail) || t("common.error")); }
  };

  const handleFillBots = async () => {
    try { await api.post(`/rooms/${roomId}/fill-bots`); }
    catch (e) { toast.error(trServer(e?.response?.data?.detail) || t("room.cantLaunch")); }
  };

  const handleLeave = async () => {
    leavingRef.current = true; // the states without the player that follow must not seat them again
    try { await api.post(`/rooms/${roomId}/leave`); } catch {}
    navigate("/lobby");
  };

  return (
    <RoomShell bannerHeight={bannerHeight} tableThemeId={tableThemeId}>
      {/* « Jouer en ligne »: strangers, a room of its own (no code, no invite, starts by itself). */}
      {room.room_type === "online" || room.room_type === "public" ? (
        <OnlineWaitingRoom
          room={room}
          user={user}
          onLeave={handleLeave}
          onStart={handleStart}
          onFillBots={handleFillBots}
        />
      ) : (
        <FriendsWaitingRoom
          room={room}
          roomId={roomId}
          user={user}
          onLeave={handleLeave}
          onStart={handleStart}
          onBots={handleBots}
        />
      )}
    </RoomShell>
  );
}

// The frame of both waiting rooms: the backdrop, the ad's band at the top (the player's felt,
// changed live by the colour grid), the content centred under it, and the language switch at the
// top left (owner's request), in a `dir="ltr"` row: it stays on the left in Tunisian too.
function RoomShell({ bannerHeight, tableThemeId, children }) {
  return (
    <div className="relative min-h-screen bg-slate-950 text-white overflow-hidden">
      <BrandBackground dim />
      {bannerHeight > 0 && (
        <div
          aria-hidden="true"
          className="rami-ad-slot fixed inset-x-0 top-0 z-40 border-b border-white/5"
          style={{ ...matStyle(tableThemeId), height: bannerHeight + 1 }}
        />
      )}

      {/* Tout le contenu : en haut de la page (owner's request), centré horizontalement, sous la bande de la pub */}
      <div
        className="relative z-10 min-h-screen flex flex-col items-center justify-start px-4 sm:px-5 pt-4 pb-6"
        style={bannerHeight > 0 ? { paddingTop: bannerHeight + 1 + 16 } : undefined}
      >
        <div dir="ltr" className="mb-2 flex w-full max-w-lg justify-start">
          <LanguageSwitch />
        </div>
        {children}
      </div>
    </div>
  );
}
